import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import SocialMediaIntegrationHub from "pages/social-media-integration-hub";
import DataCollectionMonitor from "pages/data-collection-monitor";

// Lazy load pages
const LiveDashboard = lazy(() => import("./pages/live-dashboard"));
const LiveSearchResults = lazy(() => import("./pages/live-search-results"));
const MentionsFeed = lazy(() => import("./pages/mentions-feed"));
const CompetitorAnalysis = lazy(() => import("./pages/competitor-analysis"));
const MentionDetail = lazy(() => import("./pages/mention-detail"));
const PredictiveTrends = lazy(() => import("./pages/predictive-trends"));
const PersonaIntelligence = lazy(() => import("./pages/persona-intelligence"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Auth pages
const SignIn = lazy(() => import("./pages/auth/SignIn"));
const SignUp = lazy(() => import("./pages/auth/SignUp"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));

// New pages
const Profile = lazy(() => import("./pages/Profile"));
const Integrations = lazy(() => import("./pages/Integrations"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));

const AdvancedAlertsConfiguration = lazy(() => import("./pages/advanced-alerts-configuration"));
const AIAccuracyFeedbackSystem = lazy(() => import("./pages/ai-accuracy-feedback-system"));

const LandingPage = lazy(() => import("./pages/landing-page"));
const PersonaMap = lazy(() => import("./pages/persona-map"));

const PlatformConfigurationManager = lazy(() => import("./pages/platform-configuration-manager"));

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-950">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      <div className="text-white text-sm">Loading...</div>
    </div>
  </div>
);

function ProjectRoutes() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Auth routes */}
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* App routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/landing-page" element={<LandingPage />} />
            <Route path="/live-dashboard" element={<LiveDashboard />} />
            <Route path="/live-search-results" element={<LiveSearchResults />} />
            <Route path="/mentions-feed" element={<MentionsFeed />} />
            <Route path="/competitor-analysis" element={<CompetitorAnalysis />} />
            <Route path="/mention-detail" element={<MentionDetail />} />
            <Route path="/predictive-trends" element={<PredictiveTrends />} />
            <Route path="/persona-intelligence" element={<PersonaIntelligence />} />
            <Route path="/platform-configuration-manager" element={<PlatformConfigurationManager />} />
            
            {/* New routes */}
            <Route path="/profile" element={<Profile />} />
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/persona-map" element={<PersonaMap />} />

            <Route path="/advanced-alerts-configuration" element={<AdvancedAlertsConfiguration />} />
            <Route path="/ai-accuracy-feedback-system" element={<AIAccuracyFeedbackSystem />} />
            
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
            
            <Route path="/social-media-integration-hub" element={<SocialMediaIntegrationHub />} />
            <Route path="/data-collection-monitor" element={<DataCollectionMonitor />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default ProjectRoutes;